import React, { useState } from 'react';
import * as assets from '@assets';
import { Box, IconButton, makeStyles } from '@material-ui/core';
import { Dialog } from '@material-ui/core';
import { ImageGridDialogProps } from '../types';
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  Close
} from '@material-ui/icons';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import CustomizedSwiper from '@components/Customized/CustomizedSwiper';
import { SwiperSlide } from 'swiper/react';

const ImageGridDialog = ({ open, files, onClose }: ImageGridDialogProps) => {
  const [index, setIndex] = useState(1);
  const classes = useImageGridDialogStyles();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen
      classes={{
        paperFullScreen: classes.paperFullScreen
      }}
    >
      <Box className={classes.header}>
        <CustomizedTypography
          className={'index'}
          variant="body1"
          fontWeight="bold"
          color="inherit"
        >
          {index}/{files?.length}
        </CustomizedTypography>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      <Box className={classes.contents}>
        <IconButton className="swiper-prev">
          <ChevronLeftRounded />
        </IconButton>
        <IconButton className="swiper-next">
          <ChevronRightRounded />
        </IconButton>
        <CustomizedSwiper
          slidesPerView={1}
          loop
          navigation={{
            nextEl: '.swiper-next',
            prevEl: '.swiper-prev'
          }}
          className={classes.swiperContainer}
          onSlideChange={(e) => {
            setIndex(e.realIndex + 1);
          }}
        >
          {files.map((v, i) => (
            <SwiperSlide className={classes.sipwerSlide} key={i}>
              <img
                className={classes.image}
                draggable={false}
                alt=""
                src={v.src}
                width="100%"
                height="100%"
                onError={(e) => {
                  e.currentTarget.src = assets.DefaultThumbnail;
                }}
              />
            </SwiperSlide>
          ))}
        </CustomizedSwiper>
      </Box>
    </Dialog>
  );
};

export default ImageGridDialog;

const useImageGridDialogStyles = makeStyles((theme) => ({
  swiperContainer: {
    width: '100%',
    height: 'calc(100%)'
  },
  paperFullScreen: {
    zIndex: 9999,
    backgroundColor: 'rgba(0,0,0,0.35)'
  },
  header: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorSet.gray1000,
    minHeight: 56,
    width: '100%',
    '& .index': {
      color: colorSet.gray000
    },
    '& .MuiIconButton-root': {
      position: 'absolute',
      width: 52,
      height: 52,
      padding: 2,
      right: 24,
      '& svg': {
        width: 44,
        height: 44,
        color: colorSet.gray000
      }
    }
  },
  contents: {
    width: '100%',
    height: 'calc(100vh - 56px)',
    position: 'relative',
    '& .swiper-prev': {
      left: 24
    },
    '& .swiper-next': {
      right: 24
    },
    '& .MuiIconButton-root': {
      position: 'absolute',
      zIndex: 1000,
      top: 'calc(50% - 68px)',
      width: 68,
      height: 68,
      [theme.breakpoints.down('xs')]: {
        display: 'none'
      },
      '&  svg': {
        color: colorSet.gray000,
        width: 44,
        height: 44
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)'
      }
    }
  },
  sipwerSlide: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    padding: '32px 0px',
    [theme.breakpoints.down(1280)]: {
      paddingRight: 192,
      paddingLeft: 192
    },
    [theme.breakpoints.down(960)]: {
      padding: '164px 0px',
      paddingRight: 96,
      paddingLeft: 96
    },
    [theme.breakpoints.down(600)]: {
      padding: '48px 0px',
      paddingRight: 0,
      paddingLeft: 0
    },
    [theme.breakpoints.down(375)]: {
      padding: '24px 0px'
    }
  },
  image: {
    objectFit: 'contain',
    maxWidth: 960,
    [theme.breakpoints.down(1280)]: {
      maxWidth: 896,
      maxHeight: 896
    },
    [theme.breakpoints.down(960)]: {
      maxWidth: 768,
      maxHeight: 896
    },
    [theme.breakpoints.down(600)]: {
      maxWidth: 600,
      maxHeight: 900
    }
  }
}));
